/**
 * Landing.jsx — root marketing page shown to ALL users at `/`.
 * Signed-in users see "Go to Dashboard"; guests see "Start Trial / Continue as Guest".
 *
 * Sections:
 *   1. Hero + primary CTA
 *   2. Weekly Savings Estimator (interactive)
 *   3. Smart Alternatives visual comparison
 *   4. Community Wins auto-rotating slideshow (real deals when available)
 *   5. Pricing tiers (Monthly / Annual with trial framing)
 *   6. Footer
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, ShoppingCart, TrendingDown, Users, DollarSign,
  Check, Star, Clock, Zap, ChevronLeft, ChevronRight, Utensils,
  BadgeCheck, Camera, Bell, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── PWA install button (Android Chrome + supporting browsers) ─────────
const PwaInstallButton = () => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const alreadyInstalled = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    if (alreadyInstalled) setInstalled(true);
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch { /* user dismissed */ }
      setDeferred(null);
      return;
    }
    // No native prompt available → show fallback instructions
    setShowInstructions(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-white border border-emerald-500 text-emerald-700 font-semibold text-sm hover:bg-emerald-50 shadow-sm transition-colors"
        data-testid="hero-install-pwa"
      >
        <Download className="h-4 w-4" />
        Install App
      </button>
      {showInstructions && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowInstructions(false)}
          data-testid="pwa-install-instructions-overlay"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-install-instructions"
          >
            <div className="flex items-center gap-2 text-emerald-700 mb-3">
              <Download className="h-5 w-5" />
              <h3 className="font-bold">Install Foodz Lizts</h3>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <div className="text-xs uppercase tracking-wider text-emerald-600 font-bold mb-1">Android · Chrome</div>
                <p>Tap the ⋮ menu → <strong>Install app</strong> (or <strong>Add to Home screen</strong>).</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-emerald-600 font-bold mb-1">iPhone · Safari</div>
                <p>Tap the Share button → <strong>Add to Home Screen</strong>.</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-emerald-600 font-bold mb-1">Desktop · Chrome/Edge</div>
                <p>Look for the install icon in the address bar, or use the browser menu → <strong>Install Foodz Lizts</strong>.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowInstructions(false)}
              className="mt-5 w-full py-2.5 rounded-md bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700"
              data-testid="pwa-install-instructions-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ── Hero ────────────────────────────────────────────────────────────────
const Hero = ({ onCTA, isSignedIn }) => (
  <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-white pt-10 pb-16 sm:pt-16 sm:pb-24" data-testid="landing-hero">
    <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 20% 30%, rgba(16,185,129,0.15), transparent 45%), radial-gradient(circle at 80% 60%, rgba(34,197,94,0.1), transparent 40%)",
    }} />
    <div className="relative max-w-5xl mx-auto px-4 text-center">
      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 mb-4 px-3 py-1 text-xs font-semibold">
        <Sparkles className="h-3 w-3 mr-1" /> 14-Day Free All-Access Trial
      </Badge>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4">
        Stop overpaying at the grocery store.
      </h1>
      <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
        Foodz Lizts turns your grocery list into a personalized savings plan —
        deal alerts, price comparisons, meal plans, and receipt tracking, all
        driven by your own shopping habits.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          onClick={onCTA}
          className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold px-8 py-6 text-base shadow-lg"
          data-testid="hero-primary-cta"
        >
          {isSignedIn ? "Go to Dashboard" : "Start My Free 14-Day Trial"}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        {!isSignedIn && (
          <a
            href="/auth?mode=login"
            className="inline-flex items-center gap-1.5 px-6 py-3 rounded-md border border-emerald-500 text-emerald-700 font-semibold text-sm hover:bg-emerald-50 transition-colors"
            data-testid="hero-signin-cta"
          >
            Sign in
          </a>
        )}
        {!isSignedIn && (
          <a
            href="/deals?tab=community"
            className="text-sm text-gray-600 hover:text-emerald-700 font-medium underline"
            data-testid="hero-secondary-cta"
          >
            Or continue as guest →
          </a>
        )}
      </div>
      <div className="mt-4 flex items-center justify-center">
        <PwaInstallButton />
      </div>
      <p className="text-xs text-gray-500 mt-5">No card required · Cancel anytime · Free tier stays free forever</p>
    </div>
  </section>
);

// ── Weekly Savings Estimator ────────────────────────────────────────────
const SavingsEstimator = () => {
  const [household, setHousehold] = useState(3);
  const [weekly, setWeekly] = useState(180);
  // Foodz Lizts benchmark: ~18-25% savings on personalized deal alerts + meal plans
  const est = useMemo(() => {
    const rate = 0.18 + Math.min(0.08, household * 0.01);
    const weeklySave = Math.round(weekly * rate);
    return {
      weeklySave,
      monthlySave: weeklySave * 4,
      yearlySave: weeklySave * 52,
      newBill: weekly - weeklySave,
    };
  }, [household, weekly]);

  return (
    <section className="py-16 bg-white" data-testid="savings-estimator">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <Badge className="bg-emerald-50 text-emerald-700 mb-2">Weekly Savings Estimator</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">See what Foodz Lizts saves you</h2>
          <p className="text-gray-600 text-sm sm:text-base">Based on real subscriber data · 18-26% off the average grocery bill</p>
        </div>

        <Card className="border-emerald-100 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase font-semibold text-gray-500 tracking-wider">
                  Household size
                </label>
                <input
                  type="range" min={1} max={7} step={1}
                  value={household}
                  onChange={(e) => setHousehold(Number(e.target.value))}
                  className="w-full mt-2 accent-emerald-600"
                  data-testid="estimator-household"
                />
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {household} {household === 1 ? "person" : "people"}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-semibold text-gray-500 tracking-wider">
                  Current weekly grocery bill
                </label>
                <input
                  type="range" min={50} max={500} step={10}
                  value={weekly}
                  onChange={(e) => setWeekly(Number(e.target.value))}
                  className="w-full mt-2 accent-emerald-600"
                  data-testid="estimator-weekly"
                />
                <div className="text-2xl font-bold text-gray-900 mt-1">${weekly}/week</div>
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              {[
                { label: "Every week", value: `$${est.weeklySave}`, hint: `${Math.round((est.weeklySave / weekly) * 100)}% off` },
                { label: "Every month", value: `$${est.monthlySave}`, hint: "roughly" },
                { label: "Every year", value: `$${est.yearlySave.toLocaleString()}`, hint: "back in your pocket" },
              ].map((s, i) => (
                <div
                  key={i}
                  className={`rounded-xl p-4 text-center ${i === 2 ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white" : "bg-emerald-50 border border-emerald-100"}`}
                  data-testid={`estimator-result-${i}`}
                >
                  <div className={`text-[10px] uppercase tracking-widest font-semibold ${i === 2 ? "text-emerald-100" : "text-emerald-600"}`}>
                    {s.label}
                  </div>
                  <div className={`text-2xl sm:text-3xl font-bold mt-1 ${i === 2 ? "text-white" : "text-emerald-700"}`}>
                    {s.value}
                  </div>
                  <div className={`text-xs mt-1 ${i === 2 ? "text-emerald-100" : "text-gray-500"}`}>{s.hint}</div>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-gray-500 mt-4">
              Estimates modeled on 18-26% grocery savings across weekly deal-alert users. Actual savings vary by store & season.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// ── Smart Alternatives visual comparison ────────────────────────────────
const ALT_EXAMPLES = [
  {
    original: { name: "Chicken breasts", price: 8.99, unit: "/lb" },
    swap: { name: "Chicken thighs", price: 3.49, unit: "/lb", note: "Same protein, richer flavor" },
    saving: 5.50,
  },
  {
    original: { name: "Name-brand cereal", price: 5.49, unit: "18 oz box" },
    swap: { name: "Store-brand cereal", price: 2.99, unit: "18 oz box", note: "Same recipe · verified by 340 shoppers" },
    saving: 2.50,
  },
  {
    original: { name: "Prepackaged spinach", price: 4.79, unit: "5 oz" },
    swap: { name: "Loose spinach", price: 1.99, unit: "5 oz worth", note: "Fresher · less plastic" },
    saving: 2.80,
  },
];

const SmartAlternativesSection = () => (
  <section className="py-16 bg-gradient-to-br from-gray-50 to-white" data-testid="smart-alternatives-section">
    <div className="max-w-5xl mx-auto px-4">
      <div className="text-center mb-10">
        <Badge className="bg-amber-50 text-amber-700 mb-2">Smart Alternatives</Badge>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
          We suggest cheaper swaps — automatically.
        </h2>
        <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
          Every item on your list is scanned against thousands of local deals. If there's a cheaper substitute
          that hits the same meal, we flag it before you check out.
        </p>
      </div>

      <div className="grid gap-4 sm:gap-5">
        {ALT_EXAMPLES.map((ex, i) => (
          <Card key={i} className="overflow-hidden border-emerald-100 hover:shadow-lg transition-shadow" data-testid={`alt-example-${i}`}>
            <CardContent className="p-0">
              <div className="grid sm:grid-cols-[1fr_auto_1fr] items-stretch">
                <div className="p-5 border-b sm:border-b-0 sm:border-r border-gray-100">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">You had</div>
                  <div className="font-semibold text-gray-800">{ex.original.name}</div>
                  <div className="text-lg font-bold text-gray-500 line-through mt-1">${ex.original.price.toFixed(2)}<span className="text-xs font-normal text-gray-400">{ex.original.unit}</span></div>
                </div>
                <div className="flex items-center justify-center p-4 bg-gradient-to-br from-emerald-500 to-green-600 text-white sm:min-w-[110px]">
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-100 mb-1">You save</div>
                    <div className="text-2xl font-bold" data-testid={`alt-saving-${i}`}>${ex.saving.toFixed(2)}</div>
                    <TrendingDown className="h-4 w-4 mx-auto mt-1 text-emerald-100" />
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold mb-1">We swap in</div>
                  <div className="font-semibold text-emerald-800">{ex.swap.name}</div>
                  <div className="text-lg font-bold text-emerald-700 mt-1">${ex.swap.price.toFixed(2)}<span className="text-xs font-normal text-emerald-500">{ex.swap.unit}</span></div>
                  <div className="text-[11px] text-gray-500 mt-1 italic">{ex.swap.note}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// ── Meal Plans emphasis — answering "what's for breakfast/lunch/dinner" ─
const MEAL_SLOTS = [
  { emoji: "🍳", slot: "Breakfast", tag: "5-min prep", title: "Greek yogurt parfait", detail: "Fiber + protein · sale-priced berries · $1.85/serving" },
  { emoji: "🥗", slot: "Lunch", tag: "Batch-cook Sunday", title: "Chicken Caesar bowl", detail: "600 cal · pantry-friendly · reuses last night's chicken" },
  { emoji: "🍝", slot: "Dinner", tag: "Family of 4", title: "One-pan lemon garlic salmon", detail: "38g protein · uses Aldi's $6.99 salmon deal · 20 min" },
];

const MealPlansSpotlight = () => {
  const navigate = useNavigate();
  return (
    <section className="py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" data-testid="meal-plans-spotlight">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-8">
          <Badge className="bg-amber-100 text-amber-800 mb-2">
            <Utensils className="h-3 w-3 mr-1" /> AI Meal Plans
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            "What's for breakfast, lunch, and dinner?" — solved.
          </h2>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
            Foodz Lizts builds a personalized 7-day meal plan around what's already on sale, your household size,
            allergies, and nutrition goals. Every meal auto-generates a shopping list, links to a full recipe,
            and shows the price per serving.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {MEAL_SLOTS.map((m, i) => (
            <Card key={i} className="border-amber-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`meal-slot-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-3xl" aria-hidden>{m.emoji}</span>
                  <Badge className="bg-amber-50 text-amber-700 text-[10px] uppercase tracking-wider">
                    {m.slot}
                  </Badge>
                </div>
                <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">{m.tag}</p>
                <h3 className="text-base font-bold text-gray-900 mb-1">{m.title}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{m.detail}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          {[
            { icon: ShoppingCart, label: "Auto grocery list", detail: "Every meal ports its ingredients straight into your shopping list — dedup'd and priced." },
            { icon: BadgeCheck, label: "Nutrition on every card", detail: "Protein, calories, fiber, sodium at a glance — filter by keto, high-protein, kid-friendly & more." },
            { icon: Zap, label: "Built around sales", detail: "The engine looks at local price data first, so what you cook is what's on sale that week." },
          ].map(({ icon: Icon, label, detail }, i) => (
            <div key={i} className="bg-white/70 backdrop-blur rounded-xl p-4 border border-amber-100" data-testid={`meal-feature-${i}`}>
              <Icon className="h-5 w-5 text-amber-600 mb-2" />
              <div className="text-sm font-bold text-gray-900">{label}</div>
              <div className="text-xs text-gray-600 mt-1 leading-relaxed">{detail}</div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button
            onClick={() => navigate("/auth?mode=signup")}
            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold px-6 py-5"
            data-testid="meal-plans-cta"
          >
            Build my meal plan
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <p className="text-xs text-gray-500 mt-2">Included with your 14-day All-Access trial · no card</p>
        </div>
      </div>
    </section>
  );
};

// ── Community Wins slideshow ────────────────────────────────────────────
const MOCK_WINS = [
  { title: "Buy 1 Get 1 Free strawberries", store: "Kroger", price: 3.99, original: 7.99, poster: "Marcus T.", savings: 4.00 },
  { title: "Ground beef 80/20 — $2.99/lb", store: "Meijer", price: 2.99, original: 5.49, poster: "Priya S.", savings: 2.50 },
  { title: "Whole rotisserie chicken — $4.99", store: "Costco", price: 4.99, original: 8.99, poster: "Jordan L.", savings: 4.00 },
  { title: "Family pack diapers — 40% off", store: "Target", price: 24.00, original: 40.00, poster: "Alex K.", savings: 16.00 },
];

const CommunityWinsSlideshow = () => {
  const [wins, setWins] = useState(MOCK_WINS);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // Try to hydrate with real community deals (top 4 verified)
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/community/deals?limit=8&sort=savings&status=verified`);
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data?.deals) ? data.deals : (Array.isArray(data) ? data : []);
        if (cancelled) return;
        const real = items.slice(0, 4).map((d) => ({
          title: d.title || d.description?.slice(0, 60) || "Community deal",
          store: d.store_name || "Local store",
          price: Number(d.price ?? 0),
          original: Number(d.original_price ?? 0),
          poster: (d.posted_by_username || "Community").split(/\s+/)[0],
          savings: Math.max(0, Number(d.original_price ?? 0) - Number(d.price ?? 0)),
        })).filter((x) => x.original > 0 && x.price >= 0 && x.savings > 0);
        if (real.length >= 2) setWins(real);
      } catch { /* fall back to mocks */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (wins.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % wins.length), 4500);
    return () => clearInterval(t);
  }, [wins.length]);

  const current = wins[idx] || wins[0];
  if (!current) return null;

  return (
    <section className="py-16 bg-white" data-testid="community-wins-section">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-8">
          <Badge className="bg-violet-50 text-violet-700 mb-2">Community Wins</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Real deals, from real shoppers, in your area.
          </h2>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
            Every Foodz Lizts member can post the deals they find. Verified saves get boosted so the whole community wins.
          </p>
        </div>

        <div className="relative">
          <Card className="overflow-hidden border-violet-100 shadow-lg" data-testid="community-win-card">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-violet-500 to-purple-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="h-4 w-4 text-violet-100" />
                    <span className="text-xs font-bold uppercase tracking-wide">Verified deal</span>
                  </div>
                  <span className="text-xs text-violet-100">Posted by {current.poster}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold mt-3" data-testid="community-win-title">{current.title}</h3>
                <div className="mt-2 flex items-baseline gap-3">
                  <span className="text-3xl font-bold">${current.price.toFixed(2)}</span>
                  {current.original > current.price && (
                    <span className="text-lg text-violet-200 line-through">${current.original.toFixed(2)}</span>
                  )}
                  <Badge className="bg-white/20 text-white border-white/30">
                    Save ${current.savings.toFixed(2)}
                  </Badge>
                </div>
                <div className="mt-3 text-xs text-violet-100 flex items-center gap-1">
                  <ShoppingCart className="h-3 w-3" />
                  {current.store}
                </div>
              </div>
            </CardContent>
          </Card>

          {wins.length > 1 && (
            <>
              <button
                onClick={() => setIdx((i) => (i - 1 + wins.length) % wins.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md"
                aria-label="Previous deal"
                data-testid="community-wins-prev"
              >
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              </button>
              <button
                onClick={() => setIdx((i) => (i + 1) % wins.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md"
                aria-label="Next deal"
                data-testid="community-wins-next"
              >
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
              <div className="flex justify-center gap-1.5 mt-4">
                {wins.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-violet-500" : "w-1.5 bg-gray-300"}`}
                    aria-label={`Go to deal ${i + 1}`}
                    data-testid={`community-wins-dot-${i}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// ── Pricing ─────────────────────────────────────────────────────────────
const PRICING_FEATURES_FREE = [
  "Grocery List Saver — build & manage lists",
  "Community Deals — browse, like, comment",
  "Home tab + Scan tab (feedback)",
];
const PRICING_FEATURES_PRO = [
  "Everything in Free, plus:",
  "AI-personalized deal alerts every day",
  "Cross-store weekly price analysis (Price Compare)",
  "AI meal plans built around what's on sale",
  "Receipt scanning + spend & budget insights",
  "Full recipe library + custom recipe imports",
  "Post community deals + earn rewards",
];

const PricingSection = ({ isSignedIn }) => {
  const navigate = useNavigate();
  return (
    <section className="py-16 bg-gradient-to-br from-emerald-50 to-green-50" data-testid="pricing-section">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-10">
          <Badge className="bg-emerald-100 text-emerald-800 mb-2">Pricing</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Start free. Keep saving for less than one week of coffee.
          </h2>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
            Every new account gets a full 14-day All-Access trial. After that, keep the free tier forever or subscribe for the full toolkit.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
          {/* Free */}
          <Card className="border-gray-200" data-testid="pricing-free">
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Free forever</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">$0</span>
                <span className="text-sm text-gray-500">/ month</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">Everyone gets the basics — no card, no trial expiring.</p>
              <ul className="mt-5 space-y-2">
                {PRICING_FEATURES_FREE.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="w-full mt-6 border-gray-300"
                onClick={() => navigate("/deals")}
                data-testid="pricing-free-cta"
              >
                Continue as guest
              </Button>
            </CardContent>
          </Card>

          {/* Monthly */}
          <Card className="border-emerald-400 ring-2 ring-emerald-400/30 shadow-lg relative" data-testid="pricing-monthly">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow">
                <Sparkles className="h-3 w-3 mr-1" /> Most popular
              </Badge>
            </div>
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold">All-Access Monthly</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">$4.99</span>
                <span className="text-sm text-gray-500">/ month</span>
              </div>
              <p className="text-xs text-emerald-700 font-medium mt-2">14-day free trial · Cancel anytime</p>
              <ul className="mt-5 space-y-2">
                {PRICING_FEATURES_PRO.map((f, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${i === 0 ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                    {i === 0 ? <Zap className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> : <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />}
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold"
                onClick={() => navigate(isSignedIn ? "/subscription" : "/auth?mode=signup")}
                data-testid="pricing-monthly-cta"
              >
                {isSignedIn ? "Upgrade Now" : "Start Free Trial"}
              </Button>
            </CardContent>
          </Card>

          {/* Annual */}
          <Card className="border-gray-200 relative" data-testid="pricing-annual">
            <div className="absolute -top-3 right-4">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Save 25%</Badge>
            </div>
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">All-Access Annual</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">$45</span>
                <span className="text-sm text-gray-500">/ year</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">$3.75/month · saves you $14.88/yr</p>
              <ul className="mt-5 space-y-2">
                {PRICING_FEATURES_PRO.map((f, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${i === 0 ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                    {i === 0 ? <Star className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" /> : <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />}
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="w-full mt-6 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={() => navigate(isSignedIn ? "/subscription?plan=annual" : "/auth?mode=signup")}
                data-testid="pricing-annual-cta"
              >
                {isSignedIn ? "Choose Annual" : "Start Free Trial"}
              </Button>
            </CardContent>
          </Card>
        </div>
        <p className="text-center text-xs text-gray-500 mt-6">All plans include the 14-day trial. No card is charged until day 15.</p>
      </div>
    </section>
  );
};

// ── Footer / value strip ────────────────────────────────────────────────
const ValueStrip = () => (
  <section className="py-10 bg-gray-900 text-white" data-testid="landing-footer-value">
    <div className="max-w-5xl mx-auto px-4 grid sm:grid-cols-4 gap-6 text-center">
      {[
        { icon: Users, label: "1000+", sub: "families saving" },
        { icon: DollarSign, label: "$28/wk", sub: "average savings" },
        { icon: Bell, label: "Daily", sub: "personalized deal alerts" },
        { icon: Clock, label: "14-day", sub: "free trial · no card" },
      ].map(({ icon: Icon, label, sub }, i) => (
        <div key={i}>
          <Icon className="h-6 w-6 mx-auto mb-2 text-emerald-400" />
          <div className="text-2xl font-bold">{label}</div>
          <div className="text-xs text-gray-400 mt-1">{sub}</div>
        </div>
      ))}
    </div>
  </section>
);

// ── Landing page ────────────────────────────────────────────────────────
export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSignedIn = !!user;

  const primaryCTA = () => {
    if (isSignedIn) navigate("/home");
    else navigate("/auth?mode=signup");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Hero onCTA={primaryCTA} isSignedIn={isSignedIn} />
      <SavingsEstimator />
      <SmartAlternativesSection />
      <MealPlansSpotlight />
      <CommunityWinsSlideshow />
      <PricingSection isSignedIn={isSignedIn} />
      <ValueStrip />
      <footer className="py-6 bg-gray-900 text-gray-400 text-center text-xs" data-testid="landing-legal-footer">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="/privacy" className="hover:text-white">Privacy</a>
            <a href="/terms" className="hover:text-white">Terms</a>
            <a href="/support" className="hover:text-white">Support</a>
          </div>
          <p className="mt-3">© {new Date().getFullYear()} Foodz Lizts. Built for shoppers who won't overpay.</p>
        </div>
      </footer>
    </div>
  );
}
